// User types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'production' | 'viewer' | 'employee';
  type?: 'dealer' | 'employee';
  user_type?: 'dealer_contact' | 'dealer_sales_rep' | 'employee' | 'legacy_user';
  allowed_portals?: string[];
  permissions?: {
    all_access?: boolean;
    factory_admin_access?: boolean;
    factory_admin_role?: string;
    discipline_access?: boolean;
    // Accounting permissions
    accounting_access?: boolean;
    can_approve_dealers?: boolean;
    can_manage_bills?: boolean;
    can_edit_employee_tax?: boolean;
    can_view_payroll?: boolean;
    can_view_revenue?: boolean;
    [key: string]: unknown;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Dealer Rewards types
export interface DealerRewards {
  currentPoints?: number;
  lifetimePoints?: number;
  ytdPointsEarned?: number;
  tierName?: string;
  tierDisplayName?: string;
  tierIcon?: string;
  tierColor?: string;
  tierDiscountPercent?: number;
}

// Dealer types
export interface Dealer {
  id: string;
  name: string;
  companyName?: string;
  legalName?: string;
  email: string;
  phone?: string;
  address?: string | { street?: string; city?: string; state?: string; zip?: string; country?: string };
  city?: string;
  state?: string;
  zip?: string;
  website?: string;
  logoUrl?: string;
  brandedEmail?: string;
  notes?: string;
  discountPercent: number;
  status: 'active' | 'inactive' | 'pending';
  engagebayId?: string;
  quoteRiteId?: string;
  createdAt: string;
  updatedAt: string;
  rewards?: DealerRewards;
  stats?: { total_orders: number; active_orders: number; total_spent: number };
  // Credit account fields
  credit_limit?: number;
  credit_balance?: number;
  net_account_enabled?: boolean;
  net_terms_days?: number;
  credit_account_status?: 'active' | 'frozen' | 'suspended';
  // Legacy fields used by DealersPage
  totalOrders?: number;
  totalRevenue?: number;
  sales_rep_id?: string;
  sales_rep_name?: string;
}

// Order types
export type OrderStatus =
  | 'draft'
  | 'submitted'
  | 'pending_release'
  | 'approved'
  | 'in_production'
  | 'ready'
  | 'ready_to_ship'
  | 'shipped'
  | 'delivered'
  | 'closed'
  | 'cancelled';

// Patio Visualizer config
export interface VisualizerConfig {
  fabricType?: string;
  fabricColor?: string;
  systemColor?: string;
  dimensions?: {
    width: number;
    height: number;
  };
  createdAt?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  dealerId: string;
  dealerName: string;
  status: OrderStatus;
  sidemark?: string;
  poNumber?: string;
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  depositAmount?: number;
  depositPaidAt?: string;
  paidInFullAt?: string;
  shippingMethod?: string;
  shippingAddress?: ShippingAddress;
  notes?: string;
  internalNotes?: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  releasedAt?: string;
  items?: OrderItem[];
  // Patio Visualizer fields
  visualizerConfig?: VisualizerConfig;
  visualizerImageUrl?: string;
  // Dealer retail price (what they sold to their customer)
  customerSellPrice?: number;
}

export interface ShippingAddress {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  productType: string;
  itemNumber?: number;
  unitNumber: number;
  widthTop: number;
  widthMiddle: number;
  widthBottom: number;
  heightLeft: number;
  heightCenter: number;
  heightRight: number;
  maxWidth?: number;
  maxHeight?: number;
  systemColor: string;
  fabricType: string;
  fabricColor: string;
  motorSide: 'left' | 'right';
  motorType?: string;
  uChannel: 'none' | 'one_side' | 'two_sides';
  uChannelColor?: string;
  trackMount: string;
  installationType: string;
  recessed: boolean;
  biasedCut: boolean;
  biasedCutAmount?: number;
  biasedCutInches?: number;
  boxSize?: string;
  brushSize?: string;
  unitPrice: number;
  totalPrice?: number;
  status?: string;
  notes?: string;
  expertMode?: boolean;
  applyUChannelDeduction?: boolean;
  boxWidthAdjustment?: number | null;
  applyBoxDeduction?: boolean;
  componentColors?: Record<string, string>;
  windowPanel?: boolean;
  windowOrientation?: string;
  windowCount?: number;
  windowWidth?: number;
  windowHeight?: number;
  topBorderInches?: number;
  sideBorderInches?: number;
  bottomBorderInches?: number;
  calculatedDimensions?: {
    widthSelected?: number;
    widthTop?: number;
    systemWidth?: number;
    systemHeight?: number;
    uChannelDeduction?: number;
    boxDeduction?: number;
    boxWidthAdjustment?: number;
    dealerBoxAdjustment?: number;
  };
}

// Production types
export type WorkOrderStatus = 
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'on_hold'
  | 'cancelled';

export type BlockerType =
  | 'deposit'
  | 'parts_shortage'
  | 'manual_hold'
  | 'supplier_delay'
  | 'customer_change'
  | 'quality_issue';

export interface WorkOrder {
  id: string;
  orderItemId: string;
  status: WorkOrderStatus;
  priority: number;
  scheduledDate?: string;
  startedAt?: string;
  completedAt?: string;
  assignedTo?: string;
  currentWorkstation?: string;
  blockedBy?: BlockerType;
  blockedReason?: string;
  notes?: string;
  orderItem?: OrderItem;
  order?: Order;
}

export type WorkstationStep = 
  | 'intake'
  | 'tube_cut'
  | 'fabric_cut'
  | 'assembly'
  | 'motor'
  | 'qc'
  | 'pack'
  | 'ship';

export interface WorkstationQueue {
  workstation: WorkstationStep;
  items: WorkOrderQueueItem[];
  completedToday: number;
  averageTime: number;
}

export interface WorkOrderQueueItem {
  workOrderId: string;
  orderNumber: string;
  sidemark?: string;
  dealerName: string;
  itemNumber: number;
  dimensions: string;
  fabricType: string;
  priority: number;
  scheduledDate?: string;
  isBlocked: boolean;
  blockedBy?: BlockerType;
}

// Stock Take Types for BOM rules
export type StockTakeType = 'fixed' | 'per_screen' | 'per_order' | 'calculated' | 'cut_length' | 'linear_from_roll' | 'conditional' | 'color_variant';

// Color Variants for system color matching
export type ColorVariant = 'Black' | 'Bronze' | 'White' | 'Tan' | 'Mill Finish';

// Component Types for workstation assignment.
// Backend accepts any string (legacy DB rows have values outside this list
// like "Wall Switch", "Demo", "Brush Seal"). Kept as a string alias for
// callers that still expect the type name.
export type ComponentType = string;

// Applies To conditions for BOM rules
export const APPLIES_TO_OPTIONS = [
  'all',
  // Side-specific
  'left_side',
  'right_side',
  // U-Channel options
  'u_channel_left',
  'u_channel_right',
  'u_channel_both',
  // Screw cover (wall mount, no U-channel)
  'screw_cover_left',
  'screw_cover_right',
  // Brush sizes
  'brush_1_inch',
  'brush_2_inch',
  // Fabric types
  'fabric_solar',
  'fabric_solar_premium',
  'fabric_vinyl',
  'fabric_insect',
  'fabric_window',
  // Frame colors
  'color_black',
  'color_white',
  'color_bronze',
  'color_tan',
  'color_mill_finish',
  'color_custom',
  // Motor types
  'motor_keder',
  'motor_alpha',
  'motor_somfy',
  // Other options
  'seam_required',
  'biased_cut',
  // Shipping methods
  'shipping_will_call',
  'shipping_freight',
] as const;

export type AppliesTo = typeof APPLIES_TO_OPTIONS[number];

// Inventory types
export interface InventoryItem {
  id: string;
  part_number: string;
  supplier_part_number?: string;
  description?: string;
  inventory_group?: string;
  inventoryGroup?: string;
  inventory_groups?: string[];
  inventoryGroups?: string[];
  product_lines?: string[];
  productLines?: string[];
  supplier_name?: string;
  unit_price?: number;
  on_hand: number;
  reorder_quantity?: number;
  lead_time_days?: number;
  inventory_type?: string;
  active?: boolean;
  image_filename?: string;
  created_at?: string;
  updated_at?: string;
  // BOM Rules Fields
  stock_take_type?: StockTakeType;
  quantity_formula?: string;
  cut_formula?: string;
  component_type?: ComponentType;
  applies_to?: string[];
  is_extrusion?: boolean;
  stock_length?: number;
  min_usable_drop?: number;
  roll_width?: number;
  rollWidth?: number;
  color_variant?: ColorVariant;
  // Display unit conversion for items tracked in inches but displayed differently
  display_unit?: string;
  displayUnit?: string;
  conversion_factor?: number;
  conversionFactor?: number;
  // Legacy aliases for compatibility
  sku?: string;
  name?: string;
  category?: string;
  supplier?: string;
  unitOfMeasure?: string;
  quantityOnHand?: number;
  quantityAllocated?: number;
  quantityAvailable?: number;
  reorderPoint?: number;
  unitCost?: number;
  lastReceivedAt?: string;
  location?: string;
}

// BOM Assembly Item - for assembly-based BOM rules
export const ASSEMBLY_TYPES = [
  'housing',
  'track',
  'weight_bar',
  'roller',
  'fabric',
  'motor',
  'packaging',
  'hardware',
  'open_roll',
  'spring',
  'sales_kit',
  'demo',
] as const;

export type AssemblyType = typeof ASSEMBLY_TYPES[number];

export interface BomAssemblyItem {
  id: string;
  inventoryItemId: string;
  inventoryPartNumber?: string;
  inventoryDescription?: string;
  assemblyType: AssemblyType;
  quantityFormula: string;
  cutFormula?: string;
  appliesTo?: string[];
  workstation?: string;
  colorVariant?: string;
  notes?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Positioning specifications
  motorSidePosition?: number | null;
  idlerSidePosition?: number | null;
  positionDeductionApplies?: boolean;
  positionDeductionMethod?: 'split_evenly' | 'motor_only' | 'idler_only' | 'none';
}

export const POSITION_DEDUCTION_METHODS = [
  { value: 'split_evenly', label: 'Split Evenly', description: 'Divide deduction between both sides' },
  { value: 'motor_only', label: 'Motor Only', description: 'Full deduction to motor side' },
  { value: 'idler_only', label: 'Idler Only', description: 'Full deduction to idler side' },
  { value: 'none', label: 'None', description: 'No position adjustment' },
] as const;

export interface InventoryTransaction {
  id: string;
  itemId: string;
  type: 'receive' | 'allocate' | 'consume' | 'adjust' | 'return';
  quantity: number;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  createdById: string;
  createdAt: string;
}

export interface InventoryGroup {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductLine {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  itemCount: number;
  versions?: ProductLineVersion[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductLineVersion {
  id: string;
  productLineId: string;
  versionNumber: string;
  versionName: string | null;
  effectiveDate: string;
  endDate: string | null;
  status: 'active' | 'retired' | 'warranty_only';
  changeNotes: string | null;
  bomItemCount: number;
  createdAt: string;
  updatedAt: string;
}

// Dashboard types
export interface DashboardStats {
  orders: {
    pending: number;
    inProduction: number;
    readyToShip: number;
    shippedThisWeek: number;
    pendingPayment?: number;
    pendingPaymentTotal?: number;
  };
  production: {
    scheduledToday: number;
    completedToday: number;
    blockedItems: number;
    averageLeadTime: number;
  };
  inventory: {
    lowStockItems: number;
    pendingReceiving: number;
    criticalItems?: Array<{
      partNumber: string;
      description: string;
      onHand: number;
      minQty: number;
      displayUnit?: string;
    }>;
  };
  revenue: {
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
  };
  receivables?: {
    outstandingBalance: number;
    outstandingCount: number;
    partialPaymentBalance: number;
    partialPaymentCount: number;
    pendingAchTotal: number;
    pendingAchCount: number;
    qbAdjustmentCount: number;
    submittedUnpaidTotal: number;
    submittedUnpaidCount: number;
  };
  credits?: {
    availableCreditTotal: number;
    creditDealerCount: number;
    openOverpaymentTotal: number;
    openOverpaymentCount: number;
  };
  screens?: {
    completed: number;
    inProduction: number;
    awaitingProduction: number;
    inDrafts: number;
  };
}

export interface RecentActivity {
  id: string;
  type: 'order_submitted' | 'order_shipped' | 'production_complete' | 'inventory_low';
  message: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}
